import { Template } from '@ekkogmbh/apisdk';
import {
  IconButton,
  TextField,
  Button,
  Tooltip,
  Stack,
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  Theme,
} from '@mui/material';
import { Cancel } from '@mui/icons-material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { StyledSelectField } from '../../Common/Components/Forms/StyledSelectField';
import { spacing } from '../../Common/Helper/ThemeHelper';
import useTemplateGroups from '../Providers/TemplateGroupsProvider';
import useRequest from 'src/Common/Hooks/useRequest';
import useEkko from 'src/Common/Providers/EkkoProvider';

interface Props {
  groupName: string;
  coordinate: string;
  allowAddingPages: boolean;
}

// =========================================================
// Add Page Button — handles loading / empty / ready states
// =========================================================

const addPageButtonSx = { width: '88%', margin: (theme: Theme) => spacing(theme) };
const addPageButtonDisabledSx = { ...addPageButtonSx, textTransform: 'none' };

interface AddPageButtonProps {
  isFetching: boolean;
  availableTemplates: Template[] | undefined;
  onAdd: () => void;
}

const AddPageButton = ({ isFetching, availableTemplates, onAdd }: AddPageButtonProps): React.JSX.Element => {
  const isLoaded = !isFetching && availableTemplates !== undefined;
  const hasTemplates = isLoaded && availableTemplates.length > 0;

  if (!isLoaded) {
    return (
      <Button variant={'outlined'} color={'secondary'} disabled sx={addPageButtonDisabledSx}>
        Loading Templates...
      </Button>
    );
  }

  if (!hasTemplates) {
    return (
      <Button variant={'outlined'} color={'secondary'} disabled sx={addPageButtonDisabledSx}>
        No linkable templates for this coordinate available
      </Button>
    );
  }

  return (
    <Button variant={'outlined'} color={'secondary'} sx={addPageButtonSx} onClick={onAdd}>
      add page
    </Button>
  );
};

export const LabelLinkTemplateMapper = ({ groupName, coordinate, allowAddingPages }: Props): React.JSX.Element => {
  const { api } = useEkko();
  const { templateGroups, setPage, removePage } = useTemplateGroups();
  const [availableTemplates, setAvailableTemplates] = useState<Template[]>();
  const [isFetching, startFetching] = useRequest<Template[]>();

  const currentGroup = useMemo(() => templateGroups[groupName] ?? {}, [templateGroups, groupName]);
  const pages = useMemo(() => Object.keys(currentGroup), [currentGroup]);

  const fetchTemplates = useCallback(() => {
    startFetching(
      () => api.getAvailableTemplates(coordinate, true),
      (result) => setAvailableTemplates(result),
    );
  }, [coordinate]);

  const addPage = useCallback(() => {
    const defaultTempalteName = availableTemplates?.[0]?.name;
    if (defaultTempalteName === undefined) {
      return;
    }
    const targetPage = String(Number(pages.length) + 1);
    setPage(groupName, targetPage, defaultTempalteName);
  }, [groupName, setPage, availableTemplates, pages]);

  const changePage = useCallback(
    (fromPage: string, toPage: string) => {
      const templateName = String(currentGroup[fromPage]);

      if (pages.indexOf(toPage) !== -1) {
        // do nothing if index is occupied
        return;
      } else {
        removePage(groupName, fromPage);
      }

      setPage(groupName, toPage, templateName);
    },
    [currentGroup, pages, setPage, removePage, groupName],
  );

  useEffect(() => {
    fetchTemplates();
  }, [coordinate]);

  const templateOptions = useMemo(
    () =>
      availableTemplates?.map((template) => (
        <option key={template.id} value={template.name}>
          {template.name}
        </option>
      )),
    [availableTemplates],
  );

  return (
    <Stack direction={'column'} spacing={0} alignItems={'stretch'} sx={{ width: '98%' }}>
      {pages.length > 0 && (
        <Table size={'small'}>
          <TableHead>
            <TableRow>
              <TableCell>Page</TableCell>
              <TableCell>Template</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pages.map((page, idx) => (
              <TableRow key={idx}>
                <TableCell width={'10%'}>
                  <TextField
                    variant="standard"
                    type={'number'}
                    value={page}
                    onChange={(e) => {
                      changePage(page, String(Math.max(1, Number(e.target.value))));
                    }}
                    style={{ height: '100%' }}
                  />
                </TableCell>
                <TableCell>
                  <StyledSelectField
                    native
                    disabled={isFetching}
                    variant="standard"
                    value={currentGroup[page]}
                    onChange={(e) => {
                      setPage(groupName, page, String(e.target.value));
                    }}
                    style={{ margin: 0 }}
                  >
                    {templateOptions}
                  </StyledSelectField>
                </TableCell>
                <TableCell align={'right'}>
                  <Tooltip title={`Remove Page ${page}`} placement={'right'}>
                    <IconButton
                      onClick={() => {
                        removePage(groupName, page);
                      }}
                      size="large"
                    >
                      <Cancel />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {allowAddingPages && (
        <AddPageButton isFetching={isFetching} availableTemplates={availableTemplates} onAdd={addPage} />
      )}
    </Stack>
  );
};
